import { FC, useCallback, useEffect, useMemo, useState } from 'react';

import { getAccountConfigs } from '@shared/api/accountConfig/accountConfig.controller';
import {
  AccountConfig,
  AccountEntity,
} from '@shared/api/accountConfig/accountConfig.interface';
import { updateBugoBrand } from '@shared/api/bugoBrand/bugoBrand.controller';
import { bugoBrandListHookUrl } from '@shared/api/bugoBrand/bugoBrand.hook';
import {
  BugoBrandData,
  GiftAccountConfig,
  GiftSettlementMethod,
} from '@shared/api/bugoBrand/bugoBrand.interface';
import { Button } from '@shared/components/Button';
import { LowerRole } from '@shared/types';
import {
  InputNumber,
  Popover,
  Select,
  Switch,
  Table,
  TableColumnsType,
  Tag,
  message,
} from 'antd';
import _ from 'lodash';
import { useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';

interface Props {
  bugoBrandList: BugoBrandData[];
}

type BugoBrandTableData = {
  id: string;
  name: string;
  workerNum: number;
  agencyAdmin: string[];
  giftEnabled: boolean;
  multiAgency: boolean;
  branchNum: number;
  accountConfig: AccountConfig | null;
  giftAccountConfig: GiftAccountConfig | null;
};

const DEFAULT_GIFT_ACCOUNT_CONFIG: GiftAccountConfig = {
  enabled: false,
  viaMari: false,
  method: GiftSettlementMethod.FeeRate,
  feeRate: 5.3,
};

const boolMark = (value: boolean) => {
  return value ? (
    <p className="font-bold text-blue-600">O</p>
  ) : (
    <p className="theme-text-7">X</p>
  );
};

//* 답례품 정산설정 셀 (요약 표시 + Popover 편집)
const GiftAccountConfigCell: FC<{
  brandId: string;
  config: GiftAccountConfig | null;
  saving: boolean;
  onSave: (brandId: string, config: GiftAccountConfig) => Promise<void>;
}> = function GiftAccountConfigCell({ brandId, config, saving, onSave }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<GiftAccountConfig>(
    config ?? DEFAULT_GIFT_ACCOUNT_CONFIG,
  );

  const openEditor = () => {
    setDraft(config ?? DEFAULT_GIFT_ACCOUNT_CONFIG);
    setOpen(true);
  };

  const summary = useMemo(() => {
    if (!config?.enabled) {
      return <span className="theme-text-7">미사용</span>;
    }
    return (
      <span className="center-box gap-1">
        {config.method === GiftSettlementMethod.FeeRate ? (
          <Tag color="blue" className="m-0">
            수수료 {config.feeRate ?? 5.3}%
          </Tag>
        ) : (
          <Tag color="green" className="m-0">
            리베이트
          </Tag>
        )}
        {config.viaMari ? (
          <Tag color="purple" className="m-0">
            마리경유
          </Tag>
        ) : null}
      </span>
    );
  }, [config]);

  const editor = (
    <div className="flex w-52 flex-col gap-3">
      <div className="flex items-center justify-between">
        <p>정산 사용</p>
        <Switch
          size="small"
          checked={draft.enabled}
          onChange={(checked) => setDraft((prev) => ({ ...prev, enabled: checked }))}
        />
      </div>
      <div className="flex items-center justify-between">
        <p>마리 경유</p>
        <Switch
          size="small"
          checked={draft.viaMari}
          disabled={!draft.enabled}
          onChange={(checked) => setDraft((prev) => ({ ...prev, viaMari: checked }))}
        />
      </div>
      <div className="flex items-center justify-between">
        <p>정산 방식</p>
        <Select
          size="small"
          className="w-28"
          value={draft.method}
          disabled={!draft.enabled}
          options={[
            { value: GiftSettlementMethod.FeeRate, label: '수수료율' },
            { value: GiftSettlementMethod.Rebate, label: '리베이트' },
          ]}
          onChange={(method) => setDraft((prev) => ({ ...prev, method }))}
        />
      </div>
      {draft.method === GiftSettlementMethod.FeeRate ? (
        <div className="flex items-center justify-between">
          <p>수수료율(%)</p>
          <InputNumber
            size="small"
            className="w-20"
            min={0}
            max={100}
            step={0.1}
            value={draft.feeRate ?? 5.3}
            disabled={!draft.enabled}
            onChange={(value) =>
              setDraft((prev) => ({ ...prev, feeRate: value ?? undefined }))
            }
          />
        </div>
      ) : null}
      <div className="flex justify-end gap-2">
        <button
          className="rounded border px-3 py-1 text-sm hover:bg-gray-50"
          onClick={() => setOpen(false)}
        >
          취소
        </button>
        <button
          className="rounded bg-blue-600 px-3 py-1 text-sm text-white hover:bg-blue-700 disabled:opacity-50"
          disabled={saving}
          onClick={async () => {
            await onSave(brandId, draft);
            setOpen(false);
          }}
        >
          {saving ? '저장 중...' : '저장'}
        </button>
      </div>
    </div>
  );

  return (
    <Popover
      trigger="click"
      open={open}
      onOpenChange={(visible) => (visible ? openEditor() : setOpen(false))}
      content={editor}
    >
      <button className="center-box w-full cursor-pointer gap-1 whitespace-nowrap">
        {summary}
      </button>
    </Popover>
  );
};

const accountEntityLabel = (entity: AccountEntity) => {
  switch (entity) {
    case AccountEntity.Mari:
      return '마리';
    case AccountEntity.Gaon:
      return '가온';
    case AccountEntity.NotDecided:
      return '미정';
    default:
      return entity;
  }
};

export const AdminBugoBrandListTable: FC<Props> = function AdminBugoBrandListTable({
  bugoBrandList,
}: Props) {
  const queryClient = useQueryClient();
  const [accountConfigs, setAccountConfigs] = useState<AccountConfig[]>([]);
  const [savingId, setSavingId] = useState<string | null>(null);

  useEffect(() => {
    getAccountConfigs()
      .then(setAccountConfigs)
      .catch((err) => {
        console.error(err);
      });
  }, []);

  const handleChangeAccountConfig = useCallback(
    async (brandId: string, accountConfigId: string | null) => {
      setSavingId(brandId);
      try {
        await updateBugoBrand(
          { _id: brandId, accountConfig: accountConfigId },
          LowerRole.Admin,
        );
        message.success('정산설정이 변경되었습니다.');
        await queryClient.invalidateQueries(bugoBrandListHookUrl);
      } catch (err) {
        console.error(err);
        message.error('정산설정 변경에 실패했습니다.');
      } finally {
        setSavingId(null);
      }
    },
    [queryClient],
  );

  const handleSaveGiftAccountConfig = useCallback(
    async (brandId: string, config: GiftAccountConfig) => {
      setSavingId(brandId);
      try {
        await updateBugoBrand(
          { _id: brandId, giftAccountConfig: config },
          LowerRole.Admin,
        );
        message.success('답례품 정산설정이 변경되었습니다.');
        await queryClient.invalidateQueries(bugoBrandListHookUrl);
      } catch (err) {
        console.error(err);
        message.error('답례품 정산설정 변경에 실패했습니다.');
      } finally {
        setSavingId(null);
      }
    },
    [queryClient],
  );

  const accountConfigOptions = useMemo(() => {
    return accountConfigs.map((config) => {
      return {
        value: config._id,
        label: `${config.name} (${accountEntityLabel(config.accountEntity)})`,
      };
    });
  }, [accountConfigs]);

  const columns = useMemo<TableColumnsType<BugoBrandTableData>>(
    () => [
      {
        title: '상조',
        dataIndex: 'name',
        key: 'name',
        align: 'center',
      },
      {
        title: '지도사수',
        dataIndex: 'workerNum',
        key: 'workerNum',
        align: 'center',
      },
      {
        title: '지사수',
        dataIndex: 'branchNum',
        key: 'branchNum',
        align: 'center',
      },
      {
        title: '답례품',
        dataIndex: 'giftEnabled',
        key: 'giftEnabled',
        align: 'center',
        render: (giftEnabled: boolean) => boolMark(giftEnabled),
      },
      {
        title: '멀티상조',
        dataIndex: 'multiAgency',
        key: 'multiAgency',
        align: 'center',
        render: (multiAgency: boolean) => boolMark(multiAgency),
      },
      {
        title: '관리자',
        dataIndex: 'agencyAdmin',
        key: 'agencyAdmin',
        align: 'center',
        render: (admin: string[]) => {
          return <p>{admin.length}</p>;
        },
      },
      {
        title: '정산설정',
        dataIndex: 'accountConfig',
        key: 'accountConfig',
        align: 'center',
        render: (config: AccountConfig | null, record) => {
          return (
            <div className="center-box gap-2 whitespace-nowrap">
              <Select
                className="min-w-[160px] text-left"
                size="small"
                placeholder="미설정"
                allowClear
                value={config?._id ?? undefined}
                options={accountConfigOptions}
                loading={savingId === record.id}
                disabled={savingId === record.id}
                onChange={(val) =>
                  handleChangeAccountConfig(record.id, (val as string) ?? null)
                }
              />
              {config?.pgFeeRefund ? (
                <Tag color="blue" className="m-0">
                  PG환급
                </Tag>
              ) : null}
            </div>
          );
        },
      },
      {
        title: '답례품정산',
        dataIndex: 'giftAccountConfig',
        key: 'giftAccountConfig',
        align: 'center',
        render: (config: GiftAccountConfig | null, record) => {
          return (
            <GiftAccountConfigCell
              brandId={record.id}
              config={config}
              saving={savingId === record.id}
              onSave={handleSaveGiftAccountConfig}
            />
          );
        },
      },
      {
        title: '자세히보기',
        dataIndex: 'id',
        key: 'id',
        align: 'center',
        render: (id) => {
          return (
            <div className="center-box gap-2">
              <Link to={`${id}/edit`}>
                <Button className="shadow-sm">수정</Button>
              </Link>
              <Link to={`${id}/feature-edit`}>
                <Button className="shadow-sm">기능수정</Button>
              </Link>
              <Link to={`${id}/multi-agency-edit`}>
                <Button className="shadow-sm">멀티상조</Button>
              </Link>
              <Link to={`${id}/agency-admin`}>
                <Button className="shaodw-sm">관리자</Button>
              </Link>
              <Link to={`${id}/agency-branch-admin`}>
                <Button className="shaodw-sm">지사</Button>
              </Link>
              <Link to={`${id}/agency-workers`}>
                <Button className="shaodw-sm">지도사</Button>
              </Link>
            </div>
          );
        },
      },
    ],
    [accountConfigOptions, savingId, handleChangeAccountConfig, handleSaveGiftAccountConfig],
  );

  const tableData: BugoBrandTableData[] = useMemo(() => {
    return _.map(bugoBrandList, (bugoBrand): BugoBrandTableData => {
      const branchNum = _.uniq(
        _.compact(
          bugoBrand.workers.map((worker) => {
            return worker.bugoAgencyBranchAdminDetail?.teamName;
          }),
        ),
      ).length;

      return {
        id: bugoBrand._id,
        name: bugoBrand.bugoAgency?.corpName ?? '',
        workerNum: bugoBrand.workers.length,
        agencyAdmin: bugoBrand.users,
        giftEnabled: bugoBrand.featureFlags?.giftEnabled ?? false,
        multiAgency: (bugoBrand.bugoAgencyList?.length ?? 0) > 0,
        branchNum,
        accountConfig:
          typeof bugoBrand.accountConfig === 'object' ? bugoBrand.accountConfig : null,
        giftAccountConfig: bugoBrand.giftAccountConfig ?? null,
      };
    });
  }, [bugoBrandList]);

  return (
    <div>
      <Table
        className="w-full"
        columns={columns}
        dataSource={_.flatMapDeep([tableData])}
        pagination={{
          position: ['bottomCenter'],
          defaultPageSize: 20,
          showSizeChanger: true,
          pageSizeOptions: [10, 15, 20, 50, 100],
        }}
        rowKey={'name'}
        bordered
        size={'small'}
      />
    </div>
  );
};
